import { useState, useEffect, useCallback } from "react";

export interface DraftOptions {
  storageKey?: string;
  autoSaveDelay?: number; // en millisecondes
  maxVersions?: number;
}

export interface DraftState<T> {
  data: T | null;
  version: number;
  lastSavedAt: Date | null;
  isDraft: boolean;
}

export default function useDraft<T>(
  initialData: T | null = null,
  options: DraftOptions = {}
) {
  const {
    storageKey = `draft_${new Date().getTime()}`,
    autoSaveDelay = 5000,
    maxVersions = 5,
  } = options;

  const [draft, setDraft] = useState<DraftState<T>>({
    data: initialData,
    version: 1,
    lastSavedAt: null,
    isDraft: true,
  });

  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // 🔄 Charger le brouillon depuis localStorage
  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setDraft({
          data: parsed.data,
          version: parsed.version || 1,
          lastSavedAt: parsed.lastSavedAt ? new Date(parsed.lastSavedAt) : null,
          isDraft: true,
        });
        setHasUnsavedChanges(true);
      } catch (error) {
        console.error("Erreur lors du chargement du brouillon:", error);
      }
    }
  }, [storageKey]);

  // 💾 Sauvegarder le brouillon
  const saveDraft = useCallback(
    (data: T) => {
      const newVersion = draft.version + 1;
      const newDraftState: DraftState<T> = {
        data,
        version: newVersion,
        lastSavedAt: new Date(),
        isDraft: true,
      };

      setDraft(newDraftState);
      setHasUnsavedChanges(false);

      // Sauvegarder dans localStorage
      try {
        localStorage.setItem(storageKey, JSON.stringify(newDraftState));
      } catch (error) {
        console.error("Erreur lors de la sauvegarde du brouillon:", error);
      }
    },
    [draft.version, storageKey]
  );

  // 🗑️ Supprimer le brouillon
  const clearDraft = useCallback(() => {
    const clearedState: DraftState<T> = {
      data: null,
      version: 0,
      lastSavedAt: null,
      isDraft: false,
    };
    setDraft(clearedState);
    setHasUnsavedChanges(false);
    localStorage.removeItem(storageKey);
  }, [storageKey]);

  // 📝 Mettre à jour les données sans sauvegarder
  const updateDraftData = useCallback((data: T) => {
    setDraft((prev) => ({
      ...prev,
      data,
    }));
    setHasUnsavedChanges(true);
  }, []);

  // ⏰ Auto-save
  useEffect(() => {
    if (!hasUnsavedChanges || !draft.data) return;

    const timer = setTimeout(() => {
      setIsAutoSaving(true);
      saveDraft(draft.data as any);
      setIsAutoSaving(false);
    }, autoSaveDelay);

    return () => clearTimeout(timer);
  }, [draft.data, hasUnsavedChanges, saveDraft, autoSaveDelay]);

  // 🔄 Publier (transformer en publication, pas en brouillon)
  const publishDraft = useCallback(() => {
    setDraft((prev) => ({
      ...prev,
      isDraft: false,
    }));
  }, []);

  // 📋 Vérifier si le brouillon existe
  const hasDraft = draft.data !== null && draft.isDraft;

  // 🔢 Récupérer les versions
  const getVersions = useCallback((): DraftState<T>[] => {
    const versions: DraftState<T>[] = [];
    for (let i = 1; i <= draft.version; i++) {
      const saved = localStorage.getItem(`${storageKey}_v${i}`);
      if (saved) {
        try {
          versions.push(JSON.parse(saved));
        } catch (error) {
          console.error(`Erreur lors du chargement de la version ${i}:`, error);
        }
      }
    }
    return versions;
  }, [draft.version, storageKey]);

  return {
    // Données
    draftData: draft.data,
    version: draft.version,
    lastSavedAt: draft.lastSavedAt,
    isDraft: draft.isDraft,
    hasUnsavedChanges,
    hasDraft,
    isAutoSaving,

    // Méthodes
    saveDraft,
    clearDraft,
    updateDraftData,
    publishDraft,
    getVersions,
  };
}